import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, NgZone } from '@angular/core';
import { CRouteList } from '@app/route-list.const';
import { AppConfigService } from '@core/app-config/app-config.service';
import { IDiscounts } from '@core/discounts/models/discounts.model';
import { IframeService } from '@core/iframe/iframe.service';
import { CStorageKeys } from '@core/storage/storage-keys.const';
import { StorageService } from '@core/storage/storage.service';
import { IVenueConfig } from '@core/venue-config/models/venue-config.model';
import { ScriptsService } from '@roller/rdl/utilities/scripts';
import { IDictionary } from '../common.model';
import { AnalyticsMappingService } from './analytics-mapping.service';
import { IAnalyticsWindow, ITracking } from './analytics_item.model';
import {
  IMatomoEcommerceItem,
  IMatomoEcommerceOrder,
  IMatomoSetCustomDimension,
  TMatomoEvents
} from './matomo-analytics.model';
import { IOrderItem } from '@core/order/models/order-item.model';
import { IOrder } from '@core/order/models/order.model';
import { InitialConfigService } from '@core/app-config/initial-config.service';
import { LoggerService } from '../logger/logger.service';
import { IProduct } from '../product/models/product.model';
import { environment } from '@env/environment';
import { EMatomoActionCustomDimension, MatomoSiteId } from './matomo.const';

@Injectable({
  providedIn: 'root'
})
export class MatomoAnalyticsService {
  readonly cRoutes = CRouteList;

  private window?: IAnalyticsWindow;
  private venueSlug = '';
  private checkoutName = '';
  private venueConfig: IVenueConfig | undefined;
  private isInIframe = false;
  private siteId?: string;
  private siteIdOverride: string | undefined;

  matomo: ITracking = {
    isEnabled: false,
    isScriptAdded: false
  };

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private zone: NgZone,
    private appConfigService: AppConfigService,
    private storageService: StorageService,
    private analyticsMapping: AnalyticsMappingService,
    private scriptsService: ScriptsService,
    private iframeService: IframeService,
    private initialConfigService: InitialConfigService,
    private loggerService: LoggerService
  ) {
    this.siteIdOverride = environment.production ? MatomoSiteId.production : MatomoSiteId.test;
  }

  /** Set up Matomo analytics tracking script and associated events for initialization */
  setup(config: IVenueConfig, checkoutName: string): void {
    if (!config?.analytics.matomoAnalyticsEnabled) {
      return;
    }

    this.init(config, checkoutName);

    if (!this.matomo.isScriptAdded && (this.siteId || this.siteIdOverride)) {
      const matomoScript = this.createMatomo();
      if (!matomoScript) {
        this.loggerService.warn(
          `Unable to load Matomo for '${this.venueSlug} - ${this.checkoutName}' - siteIds: '${this.siteId}', '${this.siteIdOverride}'`
        );
        return;
      }

      this.scriptsService.addScriptElement(undefined, matomoScript);
      this.matomo.isScriptAdded = true;
      this.matomo.isEnabled = true;
    }
  }

  init(config: IVenueConfig, checkoutName: string): void {
    this.venueConfig = config;
    this.checkoutName = checkoutName;
    this.window = this.document.defaultView as Window as IAnalyticsWindow;
    this.siteId = this.venueConfig.analytics.matomoSiteIds?.[this.checkoutName];
    this.isInIframe = this.iframeService.getIsInsideIframe();
    this.venueSlug = this.storageService.getItem<string>(CStorageKeys.apiKey, false) as string;
  }

  trackPageView(page_location: string, page_path: string | undefined): void {
    page_path ??= this.window?.location.pathname;
    if (page_path) {
      const cleanPath = page_path.split('?')[0].toLowerCase();
      this.callMatomo('setCustomUrl', { cleanPath });
    }

    this.callMatomo('setDocumentTitle', { page_location });
    this.callMatomo('trackPageView');
    this.resetEcommerceView();
  }

  trackEcommerceView(item: IProduct, category: string) {
    this.callMatomo('setEcommerceView', this.analyticsMapping.mapProductToMatomoSetEcommerceViewItem(item, category));
  }

  /** Handles adding and removing items to cart and sending these events to Matomo. */
  trackCartEvent(items: IOrderItem[], totalOrderValue: number): void {
    const aItems = this.analyticsMapping.mapOrderItemsToMatomoEcommerceItems(items);

    aItems.forEach((item: IMatomoEcommerceItem) => {
      if (item.quantity === 0) {
        this.callMatomo('removeEcommerceItem', { productSku: item.productSku });
      }

      if (item.quantity > 0) {
        this.callMatomo('addEcommerceItem', item);
      }
    });

    this.callMatomo('trackEcommerceCartUpdate', { totalValue: totalOrderValue });
  }

  /** On init of the confirmation page sends through the booking details and items in cart. */
  trackEcommerceOrder(order: IOrder): void {
    this.callMatomo('trackEcommerceOrder', <IMatomoEcommerceOrder>{
      orderId: order.draftBooking?.uniqueBookingId,
      grandTotal: order.amounts.total,
      subTotal: order.amounts.subTotal,
      tax: order.amounts.tax,
      shipping: order.amounts.postage,
      discount:
        order.discounts
          .map((disc: IDiscounts) => disc.discount)
          .reduce((sum: number, discount: number) => sum + discount, 0) || 0
    });
  }

  /**
   * Prevents ecommerce data being attached to all subsequent requests
   * @example checkout/details should not have product data
   */
  private resetEcommerceView() {
    this.callMatomo('setEcommerceView', {});
  }

  /** Access to the window object, runs try catch on Matomo */
  private callMatomo(event: TMatomoEvents, properties?: IDictionary<string | number | boolean>): Promise<void> | void {
    if (!this.matomo.isEnabled || !this.matomo.isScriptAdded || !this.window?._paq) {
      return;
    }

    return this.zone.runOutsideAngular(() => {
      try {
        const payload: (string | number | boolean)[] = [event];
        if (properties) {
          Object.values(properties).forEach((prop) => payload.push(prop));
        }

        return this.window?._paq.push(payload);
      } catch (error: unknown) {
        let errorMessage = 'Unknown error';
        if (error instanceof Error) {
          errorMessage = error.message;
        }

        this.loggerService.error(
          `Unable to push data to Matomo Analytics '${this.venueSlug} - ${this.checkoutName}' - siteId: '${this.siteId}'`,
          {
            message: errorMessage
          }
        );

        this.matomo.isEnabled = false;
      }
    });
  }

  private getActionCustomDimensions(): IMatomoSetCustomDimension[] {
    const isOverlay = this.isInIframe;
    const checkoutType = this.venueConfig?.checkoutType ?? 'onlineCheckout';
    const venueSlug = this.venueSlug;
    const checkoutSlug = this.checkoutName;

    const checkoutValue = `${venueSlug}|${checkoutType}|${checkoutSlug}|${isOverlay}`;

    return [{ customDimensionId: EMatomoActionCustomDimension.Checkout, customDimensionValue: checkoutValue }];
  }

  private createMatomo(): string | undefined {
    const cookiePath = this.initialConfigService.getInitialConfig().isCustomDomain
      ? `/${this.checkoutName}/`
      : `/${this.venueSlug}/${this.checkoutName}/`;

    // If available use siteIdOverride as siteId when no siteId is configured
    if (!this.siteId && this.siteIdOverride) {
      this.siteId = this.siteIdOverride;
      this.siteIdOverride = undefined;
    }

    const matomoAnalyticsUrl = this.appConfigService.getEnvValue<string>('matomoAnalyticsUrl');
    const addTestTrackerScript = this.siteIdOverride
      ? `_paq.push(['addTracker', '//${matomoAnalyticsUrl}/matomo.php', '${this.siteIdOverride}'])`
      : '';

    const customDimensionsScript = this.createMatomoCustomDimensionScript();

    return `
        var _paq = window._paq = window._paq || [];
        ${customDimensionsScript}
        _paq.push(['enableLinkTracking']);
        (function() {
          var u="//${matomoAnalyticsUrl}/";
          _paq.push(['setTrackerUrl', u+'matomo.php']);
          _paq.push(['setSiteId', '${this.siteId}']);
            ${addTestTrackerScript}
          _paq.push(['setCookiePath', '${cookiePath}']);
          var d=document, g=d.createElement('script'), s=d.getElementsByTagName('script')[0];
          g.async=true; g.src=u+'matomo.js'; s.parentNode.insertBefore(g,s);
        })();
    `;
  }

  private createMatomoCustomDimensionScript(): string {
    const actionCustomDimensions = this.getActionCustomDimensions();
    return actionCustomDimensions
      .map(
        ({ customDimensionId, customDimensionValue }) =>
          `_paq.push(['setCustomDimension', ${customDimensionId}, '${customDimensionValue}']);`
      )
      .join('\n');
  }
}
